import { colors } from '../../styles/theme';

export interface SubSection {
  key: string;
  label: string;
}

interface Props {
  sections: ReadonlyArray<SubSection>;
  currentIndex: number;
}

type SubStepState = 'done' | 'current' | 'todo';

/** Position of a section relative to the current step. */
function sectionState(idx: number, currentIndex: number): SubStepState {
  if (idx < currentIndex) return 'done';
  if (idx === currentIndex) return 'current';
  return 'todo';
}

/** Dot color for each section state. */
function dotColor(state: SubStepState): string {
  if (state === 'done') return colors.success;
  if (state === 'current') return colors.gold;
  return colors.mutedSoft;
}

/**
 * Mini progress indicator rendered inside the pending_profile form card.
 * Shows "Step N of M · Section Label" plus a horizontal track with one
 * dot per section so the client can see at a glance where they are.
 */
const OnboardingSubStepper = ({ sections, currentIndex }: Props): JSX.Element => {
  const total = sections.length;
  const current = sections[currentIndex];
  const pct = Math.round(((currentIndex + 1) / total) * 100);

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold" style={{ color: colors.inkSoft }}>
          <span style={{ color: colors.gold }}>Step {currentIndex + 1}</span>
          <span style={{ color: colors.muted }}> of {total}</span>
          <span style={{ color: colors.inkSoft }}> · {current.label}</span>
        </p>
        <p className="text-xs" style={{ color: colors.muted }}>{pct}%</p>
      </div>

      {/* Track */}
      <div
        className="w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: colors.border }}
      >
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, background: colors.gold }}
        />
      </div>

      {/* Section labels */}
      <div className="flex items-start mt-2" style={{ gap: `${100 / total}%` }}>
        {sections.map((sec, idx) => {
          const state = sectionState(idx, currentIndex);
          return (
            <div key={sec.key} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: dotColor(state) }}
              />
              <span
                className="text-[10px] text-center leading-tight truncate w-full"
                style={{
                  color: state === 'current' ? colors.inkSoft : colors.muted,
                  fontWeight: state === 'current' ? 600 : 400,
                }}
              >
                {sec.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingSubStepper;
